import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// Components
import Tile from 'dibs-search-product-tile';
import { TileTestVariant } from 'dibs-search-product-tile/exports/TileTestVariant';
import { FavoritesItem } from '../../components/global/FavoriteIcons/FavoritesItem';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { ProductPhotoItemTile } from './ProductPhotoItemTile';

// Helpers
import get from 'lodash.get';
import { trackEvent } from 'dibs-tracking';
import { LOCATION_SEARCH_BROWSE } from '../../components/global/TradeFavoritesTooltip/tradeFavoritesTooltipTracking';
import { tileMobileGridTypes, SB_MOBILE_ITEM_TILE_DATA_TAG } from '../../constants/sbConstants';

// Styles
import styles from './SharedItemTile.scss';

// Constants
import { SRC_SET_SIZES_TILE } from '../../constants/imageConstants';

const getDimensionLabel = (measurement, selectedUnit) => {
    let label;
    const size = measurement.size || {};
    const dimensionValues = (measurement.dimensions || [])
        .filter(({ unit }) => (unit ? unit === selectedUnit : true))
        .map(dimension => dimension.value);

    if (size.value) {
        label = (
            <FormattedMessage
                id="sb.SharedItemTile.sizeLabel"
                defaultMessage="Size: {size}"
                values={{
                    size: size.value,
                }}
            />
        );
    } else {
        label = dimensionValues[0];
    }

    return label;
};

export function SharedItemTileComponent(props) {
    const {
        children,
        favorites,
        index,
        item,
        isClient,
        isTestVariant,
        isMobile,
        lazyLoadStartIndex,
        onClick,
        onContentLoaded,
        showInternalSellerName,
        showSellerName,
        showCreatorName,
        showMeasurements,
        measurementUnit,
        quickViewProps,
        imageLoadVerticalOffset,
        onContentVisible,
        customTileWidthClass,
        imageSize,
        hideDivider,
        hideImagePadding,
        viewer,
        gridType,
        lastViewedItemIndex,
    } = props;
    const tileElementRef = useRef(null);
    const { activeQuickView, closeQuickView, openQuickView } = quickViewProps || {};
    const isSbMobileGridToggleDisabled = get(viewer, 'isSbMobileGridToggleDisabled') || false;
    const measurement = get(item, 'measurement') || {};
    const itemId = get(item, 'serviceId') || '';
    const itemTitle = get(item, 'title') || '';
    const showQuickView = !!openQuickView && !!closeQuickView;
    const showPhotosOnly =
        !isSbMobileGridToggleDisabled && gridType === tileMobileGridTypes.PRODUCT_PHOTO_ONLY;

    function onQuickViewDetailsClick() {
        trackEvent({
            category: 'ecommerce',
            label: itemId,
            action: 'product detail view - from seo quicklook',
        });
    }

    const favoritesComponent =
        favorites && !showPhotosOnly ? (
            <FavoritesItem
                wrapperClass={isTestVariant ? styles.favoritesTestWrapper : styles.favoritesWrapper}
                heartIconWrapperClass={styles.heartIconWrapper}
                folderIconWrapperClass={
                    isTestVariant ? styles.testFolderIconWrapper : styles.folderIconWrapper
                }
                itemId={itemId}
                itemTitle={itemTitle}
                fetchFolder={false}
                showTooltipOnFirstClick
                tooltipTrackingLabel={LOCATION_SEARCH_BROWSE}
                {...favorites}
            />
        ) : null;

    useEffect(() => {
        if (
            !isSbMobileGridToggleDisabled &&
            window.scrollY &&
            index === lastViewedItemIndex &&
            tileElementRef &&
            tileElementRef.current
        ) {
            tileElementRef.current.scrollIntoView({ block: 'center' });
        }
    }, [gridType, index, tileElementRef, lastViewedItemIndex, isSbMobileGridToggleDisabled]);

    return (
        <CurrencyProvider
            render={({ currency, showCurrency }) => {
                const sharedProps = {
                    index,
                    item,
                    itemsPerRow: lazyLoadStartIndex,
                    imageLoadVerticalOffset,
                    activeQuickView,
                    isMobile,
                    onContentLoaded,
                    onContentVisible,
                    openQuickView,
                    onClick,
                    closeQuickView,
                    srcSetSizes: SRC_SET_SIZES_TILE,
                    imageSize,
                    useLoFiLazyLoader: true,
                    showQuickViewIcon: !!isClient,
                    showSellerName,
                    showCreatorName,
                    showInternalSellerName,
                    currency: currency,
                    showPrice: isClient && showCurrency,
                };
                const tileEl = isTestVariant ? (
                    <TileTestVariant
                        {...sharedProps}
                        onQuickViewDetailsClick={onQuickViewDetailsClick}
                        favoritesComponent={favoritesComponent}
                        showMeasurements={showMeasurements}
                        showBrowseSimilar={isClient}
                        viewer={viewer}
                    />
                ) : (
                    <Tile
                        {...sharedProps}
                        showByLine={!isMobile}
                        dimensions={
                            showMeasurements ? getDimensionLabel(measurement, measurementUnit) : ''
                        }
                        showQuickView={showQuickView}
                        hideDivider={hideDivider}
                        hideImagePadding={hideImagePadding}
                        viewer={viewer}
                    />
                );
                return (
                    <div
                        className={classnames(
                            { [styles.tileWrapper]: !isTestVariant },
                            { [styles.testTileWrapper]: isTestVariant },
                            { [styles.tileWidth]: !customTileWidthClass },
                            { [customTileWidthClass]: !!customTileWidthClass },
                            { [styles.photosOnly]: showPhotosOnly },
                            { [styles.completeInfo]: !showPhotosOnly }
                        )}
                        data-tn={SB_MOBILE_ITEM_TILE_DATA_TAG}
                        data-tile-index={index}
                        ref={tileElementRef}
                    >
                        {!isTestVariant && favoritesComponent}
                        {showPhotosOnly ? (
                            <ProductPhotoItemTile
                                item={item}
                                gridType={gridType}
                                favorites={favorites}
                                index={index}
                                lazyLoadStartIndex={lazyLoadStartIndex}
                                imageLoadVerticalOffset={imageLoadVerticalOffset}
                                onClick={onClick}
                                onContentLoaded={onContentLoaded}
                                imageSize={imageSize}
                                lastViewedItemIndex={lastViewedItemIndex}
                            />
                        ) : (
                            tileEl
                        )}
                        {!isTestVariant && (
                            <React.Fragment>
                                {typeof children === 'function'
                                    ? children({ tileHoveredClass: styles.tileHovered })
                                    : children}
                            </React.Fragment>
                        )}
                    </div>
                );
            }}
        />
    );
}

SharedItemTileComponent.defaultProps = {
    isMobile: true,
    lazyLoadStartIndex: 2,
    size: 'medium',
    shippingBadge: null,
    gridType: tileMobileGridTypes.COMPLETE_PRODUCT_INFO,
    lastViewedItemIndex: 0,
};

SharedItemTileComponent.propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    favorites: PropTypes.object,
    quickViewProps: PropTypes.shape({
        activeQuickView: PropTypes.number,
        closeQuickView: PropTypes.func,
        openQuickView: PropTypes.func,
    }),
    index: PropTypes.number.isRequired,
    isMobile: PropTypes.bool,
    isTestVariant: PropTypes.bool,
    item: PropTypes.object.isRequired,
    lazyLoadStartIndex: PropTypes.number,
    imageLoadVerticalOffset: PropTypes.number,
    onClick: PropTypes.func,
    onContentLoaded: PropTypes.func,
    showInternalSellerName: PropTypes.bool,
    showSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    measurementUnit: PropTypes.string,
    isClient: PropTypes.bool.isRequired,
    onContentVisible: PropTypes.func,
    customTileWidthClass: PropTypes.string,
    imageSize: PropTypes.string,
    hideDivider: PropTypes.bool,
    hideImagePadding: PropTypes.bool,
    viewer: PropTypes.object,
    gridType: PropTypes.oneOf([
        tileMobileGridTypes.COMPLETE_PRODUCT_INFO,
        tileMobileGridTypes.PRODUCT_PHOTO_ONLY,
    ]),
    lastViewedItemIndex: PropTypes.number,
};

export const SharedItemTile = createFragmentContainer(SharedItemTileComponent, {
    viewer: graphql`
        fragment SharedItemTile_viewer on Viewer {
            isSbMobileGridToggleDisabled: isEnabled(key: "isSbMobileGridToggleDisabled")
            ...Tile_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            ...TileTestVariant_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
        }
    `,
    item: graphql`
        fragment SharedItemTile_item on Item {
            ...Tile_item @arguments(showSeller: $showSeller, isTrade: $isTrade)
            ...TileTestVariant_item @arguments(showSeller: $showSeller, isTrade: $isTrade)
            ...ProductPhotoItemTile_item
            serviceId
            title
            measurement {
                dimensions: display(
                    addLabel: true
                    separator: " "
                    fields: [height, width, depth, diameter]
                ) {
                    unit
                    value
                }
                size {
                    value
                }
            }
            browseUrl
            categoryCode
            productPhotos: photos(limit: 1) {
                placeholder
                smallPath
                masterOrZoomPath
                versions {
                    webPath
                }
            }
        }
    `,
});
